/* eslint-disable max-lines -- Helper functions */
import { v4 as uuidv4 } from "uuid";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { defaultData as BadgeDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/BadgeBlock/BadgeBlockDefaultData";
import { defaultData as BadgeListDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/BadgeListBlock/BadgeListBlockDefaultData";
import { defaultData as BlockquoteDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/BlockquoteBlock/BlockquoteDefaultData";
import { defaultData as CustomButtonDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/CustomButtonBlock/CustomButtonBlockDefaultData";
import { defaultData as LinkDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/LinkBlock/LinkBlockDefaultData";
import { defaultData as PDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/PBlock/PBlockDefaultData";
import { defaultData as ButtonDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/PrimaryButtonBlock/PrimaryButtonBlockDefaultData";
import { defaultData as QuoteDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/Quote/QuoteDefaultData";
import { defaultData as SecondaryButtonDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/SecondaryButtonBlock/SecondaryButtonBlockDefaultData";
import { defaultData as UlDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/UlListBlock/UlBlockDefaultData";
import { defaultData as H1DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Headings/H1Block/H1BlockDefaultData";
import { defaultData as H2DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Headings/H2Block/H2BlockDefaultData";
import { defaultData as H3DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Headings/H3Block/H3BlockDefaultData";
import { defaultData as H4DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Headings/H4Block/H4BlockDefaultData";
import { defaultData as H5DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Headings/H5Block/H5BlockDefaultData";
import { defaultData as H6DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Headings/H6Block/H6BlockDefaultData";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import type { ParagraphStyle } from "../Content/PBlock/PBlock";

const createBaseTextBlock = ({
  text,
  baseData,
  paragraphStyle,
  styles = {},
}: {
  text: string;
  baseData: BaseBlock;
  paragraphStyle?: ParagraphStyle;
  styles?: Record<string, StyleValue>;
}): BaseBlock => ({
  ...baseData,
  id: uuidv4(),
  props: {
    ...baseData.props,
    text: {
      ...baseData.props.text,
      prop: "other",
      value: text,
    },
    ...(paragraphStyle && {
      paragraphStyle: {
        ...baseData.props.paragraphStyle,
        prop: "other",
        value: paragraphStyle,
      },
    }),
    styles: {
      ...baseData?.props?.styles,
      prop: "styles",
      type: "styles",
      value: {
        ...(baseData?.props?.styles?.value as Record<string, StyleValue>),
        ...styles,
      },
    },
  },
});

export const createH1Block = ({
  text,
  baseData = H1DefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createH2Block = ({
  text,
  baseData = H2DefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createH3Block = ({
  text,
  baseData = H3DefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createH4Block = ({
  text,
  baseData = H4DefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createH5Block = ({
  text,
  baseData = H5DefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createH6Block = ({
  text,
  baseData = H6DefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createPBlock = ({
  text,
  baseData = PDefaultData,
  paragraphStyle,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  paragraphStyle?: ParagraphStyle;
  styles?: Record<string, StyleValue>;
}): BaseBlock =>
  createBaseTextBlock({ text, paragraphStyle, baseData, styles });

export const createBlockquoteBlock = ({
  text,
  baseData = BlockquoteDefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createQuoteBlock = ({
  text,
  baseData = QuoteDefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createBadgeBlock = ({
  text,
  baseData = BadgeDefaultData,
  styles = {},
}: {
  text: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => createBaseTextBlock({ text, baseData, styles });

export const createBadgeListBlock = ({
  items,
  baseData = BadgeListDefaultData,
  badgeStyles = {},
}: {
  items: string[];
  baseData?: BaseBlock;
  badgeStyles?: Record<string, StyleValue>;
}): BaseBlock => ({
  ...baseData,
  id: uuidv4(),
  props: {
    ...baseData.props,
    content: {
      ...baseData.props.content,
      prop: "other",
      value: items.map(value =>
        createBadgeBlock({
          text: value,
          styles: {
            ...(BadgeDefaultData?.props?.styles?.value as Record<
              string,
              StyleValue
            >),
            ...badgeStyles,
          },
        }),
      ),
    },
  },
});

export const createUlBlock = ({
  items,
  baseData = UlDefaultData,
  styles = {},
}: {
  items:
    | string[]
    | {
        text: string;
        placeholder?: string;
      }[];
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => ({
  ...baseData,
  id: uuidv4(),
  props: {
    ...baseData.props,
    items: {
      ...baseData.props.items,
      prop: "other",
      value: items.map(value => ({
        id: uuidv4(),
        value: typeof value === "string" ? value : value.text,
        bullet: {
          placeholder: typeof value === "string" ? "" : value.placeholder || "",
        },
      })),
    },
    styles: {
      ...baseData?.props?.styles,
      prop: "styles",
      type: "styles",
      value: {
        ...(baseData?.props?.styles?.value as Record<string, StyleValue>),
        ...styles,
      },
    },
  },
});

export const createLinkBlock = ({
  text,
  href,
  baseData = LinkDefaultData,
  styles = {},
}: {
  text: string;
  href: string;
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
}): BaseBlock => ({
  ...baseData,
  id: uuidv4(),
  props: {
    ...baseData.props,
    text: {
      ...baseData.props.text,
      prop: "other",
      value: text,
    },
    href: {
      ...baseData.props.href,
      prop: "other",
      value: href,
    },
    styles: {
      ...baseData?.props?.styles,
      prop: "styles",
      type: "styles",
      value: {
        ...(baseData?.props?.styles?.value as Record<string, StyleValue>),
        ...styles,
      },
    },
  },
});

export const createButtonBlock = ({
  text,
  href,
  target = "_self",
  buttonType = "primaryButton",
  styles = {},
}: {
  text: string;
  href: string;
  target?: string;
  buttonType?: "primaryButton" | "secondaryButton" | "customButton";
  styles?:
    | Record<string, StyleValue>
    | Record<string, Record<string, StyleValue>>;
}): BaseBlock => {
  const defaultData =
    buttonType === "primaryButton"
      ? ButtonDefaultData
      : buttonType === "secondaryButton"
        ? SecondaryButtonDefaultData
        : CustomButtonDefaultData;

  return {
    ...defaultData,
    id: uuidv4(),
    props: {
      ...defaultData.props,
      text: {
        ...defaultData.props.text,
        prop: "other",
        value: text,
      },
      href: {
        ...defaultData.props.href,
        prop: "other",
        value: href,
      },
      target: {
        ...defaultData.props.target,
        prop: "other",
        value: target,
      },
      buttonType: {
        ...defaultData.props.buttonType,
        prop: "other",
        value: buttonType,
      },
      styles: {
        ...defaultData?.props?.styles,
        prop: "styles",
        type: "styles",
        value: {
          ...(defaultData?.props?.styles?.value as Record<string, StyleValue>),
          ...styles,
        },
      },
    },
  };
};
